import { type FC, useEffect, useMemo, useState } from "react";
import { Dialog, LinearProgress, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import moment from "moment-timezone";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  usePromocodeQuery,
  useUpdatePromocodeMutation,
} from "@/partner/entities/promocodes";
import type {
  CreatePromocodeInputType,
  PromocodeQueryType,
} from "@/types/graphql/partner.generated";
import { PromocodeForm, type PromocodeFormValues } from "./PromocodeForm";

interface EditPromocodeDialogProps {
  open: boolean;
  promocodeId: string | null;
  onClose: () => void;
  onCompleted: () => void;
}

function mapPromocodeToFormValues(
  promocode: NonNullable<PromocodeQueryType["promocode"]>
): Partial<PromocodeFormValues> {
  return {
    title: promocode.title,
    code: promocode.code,
    productIds: promocode.productIds,
    limit: promocode.limit != null ? String(promocode.limit) : "",
    discount: promocode.discount != null ? String(promocode.discount) : "",
    amount: promocode.amount != null ? String(promocode.amount) : "",
    type: promocode.type,
    promocodeCampaignId: promocode.promocodeCampaign?.id ?? null,
    promocodeCampaignTitle: "",
    createNewCampaign: false,
    trialPeriod:
      promocode.trialPeriod != null ? String(promocode.trialPeriod) : "",
    startAt: promocode.startAt ? moment(promocode.startAt) : null,
    finishAt: promocode.finishAt ? moment(promocode.finishAt) : null,
  };
}

/**
 * Диалог редактирования промокода (référence: EditProductDialog.tsx —
 * network-only, LinearProgress пока грузится, форма рендерится только когда
 * данные пришли). UpdatePromocodeInputType не содержит code/type (Promocode
 * их не позволяет менять после создания — PromocodeValidator на бэкенде), а
 * PromocodeForm их всё равно собирает (ToggleButtonGroup/TextField
 * disabled={isEdit}) — вырезаем оба поля перед мутацией.
 */
export const EditPromocodeDialog: FC<EditPromocodeDialogProps> = ({
  open,
  promocodeId,
  onClose,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Спека §2.7: ошибка не должна переживать закрытие/повторное открытие
  // диалога — компонент смонтирован всегда (`open` только у `Dialog`).
  useEffect(() => {
    setErrorMessage(null);
  }, [open]);

  const { data, loading } = usePromocodeQuery({
    fetchPolicy: "network-only",
    variables: { id: String(promocodeId) },
    skip: !promocodeId || !open,
    // Спека §2.8: ошибка загрузки — снекбар с текстом ошибки и закрытие
    // диалога (référence EditPromocodeDialog.js: onError → enqueueSnackbar).
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
      onClose();
    },
  });

  const [updatePromocode, { loading: updating }] = useUpdatePromocodeMutation({
    // "Promocode" убран: после закрытия диалога запрос Promocode переходит в
    // skip, рефетч по его имени — холостой (Apollo предупреждает об
    // отсутствии активных наблюдателей). При повторном открытии
    // fetchPolicy: "network-only" и так тянет свежие данные.
    refetchQueries: ["PromocodesList"],
    onCompleted: () => {
      setErrorMessage(null);
      enqueueSnackbar(t("promocodes.dialog.updated"), { variant: "success" });
      onCompleted();
    },
    onError: (error) => {
      setErrorMessage(error.message);
    },
  });

  const initialValues = useMemo(
    () =>
      data?.promocode ? mapPromocodeToFormValues(data.promocode) : undefined,
    [data]
  );

  const handleSubmit = (input: CreatePromocodeInputType) => {
    // UpdatePromocodeInputType не содержит code/type — Promocode.code/type
    // неизменяемы после создания (см. PromocodeValidator на бэкенде),
    // отправка лишних полей GraphQL отклонит запрос целиком.
    const {
      title,
      promocodeCampaignId,
      promocodeCampaignTitle,
      productIds,
      limit,
      discount,
      amount,
      trialPeriod,
      startAt,
      finishAt,
    } = input;
    void updatePromocode({
      variables: {
        id: String(promocodeId),
        input: {
          title,
          promocodeCampaignId,
          promocodeCampaignTitle,
          productIds,
          limit,
          discount,
          amount,
          trialPeriod,
          startAt,
          finishAt,
        },
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="body"
      fullScreen={fullScreen}
    >
      {loading && <LinearProgress />}

      {data?.promocode && (
        <PromocodeForm
          title={t("promocodes.dialog.editTitle")}
          submitLabel={t("common.actions.save")}
          loading={updating}
          error={errorMessage}
          initialValues={initialValues}
          metrics={{
            activations: data.promocode.activations,
            totalRevenue: data.promocode.totalRevenue,
          }}
          onSubmit={handleSubmit}
          onClose={onClose}
        />
      )}
    </Dialog>
  );
};
